import type { Message as MessageSchema, Role } from '@magic/schemas/messages';
import styled, { css } from 'styled-components';

import { Markdown } from '../markdown';
import { formatTimestamp } from '../timestamp';

const Content = styled.div<{ role?: Role }>`
  border-radius: var(--border-radius-rounded);
  padding: var(--size-spacing-04);
  background-color: var(--neutral-surface-low-emphasis);
  overflow-wrap: break-word;
  word-break: break-word;
  color: inherit;
  width: auto;
  max-width: 90%;

  ${({ role }) =>
    role === 'user'
      ? css`
          color: var(--neutral-text-inverse);
          background-color: var(--neutral-surface-inverse);
          align-self: end;
          align-items: end;
        `
      : css`
          align-self: start;
          align-items: start;
        `}
`;

const Container = styled.div<{ role?: Role }>`
  display: flex;
  flex-direction: column;
  gap: var(--size-spacing-02);

  ${({ role }) =>
    role === 'user'
      ? css`
          align-self: end;
          align-items: end;
        `
      : css`
          align-self: start;
          align-items: start;
        `}
`;

export type MessageContainerProps = {
  role: Role;
  createdAt?: Date;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
};

export const MessageContainer = ({
  role,
  children,
  footer,
  createdAt,
  className = '',
}: MessageContainerProps) => {
  return (
    <Container role={role}>
      <Content
        role={role}
        className={`${role === 'user' ? 'prose prose-invert' : 'prose'} ${className}`}
        title={createdAt ? formatTimestamp(createdAt) : undefined}
      >
        {children}
      </Content>
      {footer}
    </Container>
  );
};

export type MessageProps = {
  footer?: React.ReactNode;
  message: MessageSchema;
  className?: string;
};

export const Message = ({ message, footer, className }: MessageProps) => {
  const { role, createdAt, content } = message;

  if (!content || (message.role !== 'user' && message.role !== 'assistant')) {
    return null;
  }

  return (
    <MessageContainer role={role} footer={footer} createdAt={createdAt} className={className}>
      <Markdown>{content!}</Markdown>
    </MessageContainer>
  );
};
